<template>
  <Card
    :title="title"
    flatToolBar
    :colorToolBar="color"
    :style="seriesStyle"
    minW="widthScreen < 600 ? 320 : null"
  >
    <template #content>
      <v-container>
        <v-form>
          <v-row>
            <v-col cols="12">
              <SelectWithTreeview
                v-model="serie.component"
                :label="$t('graphics.series.components')"
                :items="componentsItems"
                :loading="treeLoading"
                :required="true"
                :itemValue="'ref'"
                isOutlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <VSelect
                v-model="serie.analysisType"
                :label="$t('graphics.series.analysis')"
                itemValue="key"
                :rules="{ required: true }"
                :items="componentsAnalysisToDisplay"
                outlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <p>{{ $t('graphics.series.filter') }}*</p>
              <v-radio-group
                v-model="serie.filter"
                row
                class="d-flex flex-nowrap"
              >
                <v-radio
                  v-for="filter in filters"
                  :key="filter.key"
                  :label="filter.name"
                  :value="filter.key"
                  class="mr-4"
                  :disabled="isFilterDisabled(filter.key)"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col
              cols="12"
              md="6"
              class="d-flex justify-center"
            >
              <v-btn
                elevation="1"
                class="white--text"
                :color="color"
                @click="$emit('updateOneSeries', serie)"
              >
                {{ $t('graphics.update') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </template>

    <template
      #items
    >
      <v-menu offsetY>
        <template #activator="{ on }">
          <div class="d-flex align-center">
            <v-btn
              icon
              v-on="on"
            >
              <v-icon color="#fff">
                mdi-dots-vertical
              </v-icon>
            </v-btn>
          </div>
        </template>
        <v-list>
          <ListItem
            icon="mdi-trash-can-outline"
            :title="$t('graphics.delete')"
            color="#ef4135"
            titleColor="#ef4135"
            @click="showModalDeleteSerie"
          />
        </v-list>
      </v-menu>
    </template>
  </Card>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import SelectWithTreeview from '@/components/shared/widgets/forms/SelectWithTreeview.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import filters from '@/constants/seriesFilter';
import ListItem from '@/components/shared/widgets/ListItem.vue';
import Card from '@/components/card/Card.vue';
import treeMixin from '@/mixins/TreeMixin';

export default {
    name: 'SerieForm',
    components: {
        SelectWithTreeview,
        VSelect,
        Card,
        ListItem,
    },
    mixins: [
        treeMixin,
    ],
    props: {
        serie: {
            type: Object,
            default: null,
        },
        title: {
            type: String,
            default: null,
        },
        color: {
            type: String,
            default: null,
        },
        graphic: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            componentsItems: [],
            componentsAnalysis: [],
            treeLoading: true,
            analysis: [],
            labAnalysis: [],
            controllerAnalysis: [],
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'teamMainLang',
        ]),
        componentsAnalysisToDisplay() {
            return this.componentsAnalysis.map(({ id, name }) => ({ key: id, name: name[this.lang] || name[this.teamMainLang] || name }));
        },
        filters() {
            return Object.keys(filters).map(id => ({ key: filters[id], name: this.$t(`graphics.series.label.${filters[id]}`) }));
        },
        widthScreen() {
            return window.innerWidth;
        },
        seriesStyle() {
            return {
                outline: `1px solid ${this.color}`,
                borderRadius: '3px',
            };
        },
    },
    watch: {
        'serie.component': {
            async handler(id) {
                await this.setComponentsAnalysis(id, this.serie.filter);
            },
            deep: true,
        },
        'serie.filter': {
            async handler(filter) {
                this.changeComponentAnalysis(filter);
            },
        },
        lang: {
            async handler() {
                await this.getComponentsItems();
            },
        },
    },
    async created() {
        await this.getComponentsItems();
        await this.setComponentsAnalysis(this.serie.component, this.serie.filter);
    },
    methods: {
        resetAnalysisTypes() {
            this.analysis = [];
            this.labAnalysis = [];
            this.controllerAnalysis = [];
            this.componentsAnalysis = [];
        },
        isFilterDisabled(filterKey) {
            if (filterKey === filters.LAB) {
                return this.labAnalysis.length === 0;
            } if (filterKey === filters.CONTROLLER) {
                return this.controllerAnalysis.length === 0;
            }
            return this.analysis.length === 0;
        },
        changeComponentAnalysis(filter) {
            if (filter === filters.LAB) {
                this.componentsAnalysis = this.labAnalysis;
            } else if (filter === filters.CONTROLLER) {
                this.componentsAnalysis = this.controllerAnalysis;
            } else {
                this.componentsAnalysis = this.analysis;
            }
        },

        async getComponentsItems() {
            const { siteId } = this.$route.params;
            const { data } = await this.$rq.getNodeTree(siteId, 'site');
            this.addNodeWithEachName(data.tree);
            this.treeLoading = false;
            this.componentsItems = data.tree;
        },
        showModalDeleteSerie() {
            this.$root.$emit('showModal', {
                title: this.$t('graphics.series.deleteSerie'),
                component: Vue.component('confirmGraphicDelete', ConfirmForm),
                payload: { msg: this.$t('graphics.series.deleteMsgConfirmation') },
                onConfirm: () => this.deleteSerie(),
                isForm: false,
            });
        },
        async deleteSerie() {
            try {
                const { siteId } = this.$route.params;
                const { id, graphicId } = this.serie;
                await this.$rq.deleteSerie(siteId, graphicId, id);
                this.$emit('reRenderSeries');
                this.$emit('getSeriesData', graphicId);
            } catch (err) {
                return err;
            }
            return null;
        },
        async setComponentsAnalysis(id, filter) {
            if (id) {
                this.resetAnalysisTypes();
                await this.getComponentAnalysisTypes(id);
                this.labAnalysis = await this.getComponentLabAnalysisTypes(id, {
                    recurrency: this.graphic.recurrency,
                    startDate: this.graphic.dateFrom,
                    endDate: this.graphic.dateTo,
                });
                let analysisTypes = [...this.analysis];
                if (filter === filters.LAB) {
                    analysisTypes = [...this.labAnalysis];
                } else if (filter === filters.CONTROLLER) {
                    analysisTypes = [...this.controllerAnalysis];
                }
                if (analysisTypes.length) {
                    const uniqueAnalysisTypes = this.uniqueArray(analysisTypes);
                    this.componentsAnalysis = uniqueAnalysisTypes;
                }
            }
        },
        async getComponentLabAnalysisTypes(componentId, params) {
            const { data: labAnalysisType } = await this.$rq.getComponentLabAnalysis(componentId, params);
            return labAnalysisType;
        },
        async getComponentAnalysisTypes(componentId) {
            if (!componentId) return;
            const { data: { configurations } } = await this.$rq.getComponentConfigurations(componentId);
            if (configurations) {
                configurations.forEach(config => {
                    const { isController, productMeter, flowMeter, fuelMeter, analysisType: { id, name } } = config;
                    const item = {
                        id,
                        name: productMeter?.siteProduct?.product?.name || flowMeter?.name || fuelMeter?.fuelMeterName || name,
                    };
                    if (isController) {
                        this.controllerAnalysis.push(item);
                    } else {
                        this.analysis.push(item);
                    }
                });
            }
        },

        uniqueArray(array) {
            if (!array.length) return [];
            const uniqueIds = Array.from(new Set(array.map(item => item.id)));
            return uniqueIds.map(id => ({ id, name: array.find(a => a.id === id).name }));
        },
    },
};
</script>
<style scoped>
.row {
  margin-bottom: -30px;
}
.box{
  border: 1px solid color;
}
</style>
